import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import type { DialogActionValue } from '@/components/create-dialog'
import { Label, RadioGroup, RadioGroupItem } from '@/components/ui'
import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useSuspenseEmpreendimentoFolders } from '@/features/empreendimentos/shared/hooks/use-suspense-empreendimento-folders'

interface DialogSelecionarArquivoContentProps {
  idExterno: number
  arquivoSelecionado: { url: string; nome: string }
  onArquivoSelecionadoChange: (arquivo: { url: string; nome: string }) => void
  onAction: (value: DialogActionValue) => void
}

export const DialogSelecionarArquivoContent = ({
  idExterno,
  arquivoSelecionado,
  onArquivoSelecionadoChange,
  onAction,
}: DialogSelecionarArquivoContentProps) => {
  const { t } = useTranslation('empreendimentos')
  const { data: folders } = useSuspenseEmpreendimentoFolders(idExterno)

  const handleSelectionChange = (uri: string) => {
    const selectedFolder = folders.find((folder) => folder.uri === uri)
    if (selectedFolder) {
      onArquivoSelecionadoChange({
        url: uri,
        nome: selectedFolder.nome,
      })
    }
  }

  useEffect(() => {
    if (folders.length === 1 && folders[0]) {
      onArquivoSelecionadoChange({
        url: folders[0].uri,
        nome: folders[0].nome,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <AlertDialogHeader className="space-y-2">
        <AlertDialogTitle className="text-xl tracking-wider">
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.titulo')}
        </AlertDialogTitle>
        <AlertDialogDescription className="sr-only">
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.descricao')}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <RadioGroup
        className="-mx-6 border-t border-b border-neutral-100 px-6 py-4"
        value={arquivoSelecionado.url}
        onValueChange={handleSelectionChange}
      >
        {folders.map((folder) => (
          <div className="flex items-center gap-2 p-2" key={folder.uri}>
            <RadioGroupItem value={folder.uri} id={folder.uri} />
            <Label className="font-nunito text-base font-normal tracking-wide text-neutral-700" htmlFor={folder.uri}>
              {folder.nome}
            </Label>
          </div>
        ))}
      </RadioGroup>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={() => onAction('cancel')}>
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.botao_cancelar')}
        </AlertDialogCancel>
        <AlertDialogAction onClick={() => onAction('confirm')} disabled={!arquivoSelecionado.url}>
          {t('pages.detalhes.secoes.midia.dialog_selecionar_arquivo.botao_selecionar')}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}
